import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { createClient } from '@supabase/supabase-js';
import {
  Shield,
  Lock,
  Key,
  Smartphone,
  Eye,
  ArrowLeft,
  Save,
  AlertCircle,
  Clock,
  Globe,
  UserX,
  CheckCircle2,
  Sun,
  Moon,
  X,
  MapPin,
  Monitor,
  Code
} from 'lucide-react';

// Supabase client initialization (client-side operations; not for admin auth deletion)
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// Modüler silme kuralları: verification_codes ve user_profiles için email, diğerleri için user_id
const tableDeletionRules = (userProfile) => ({
  verification_codes: { column: 'email', value: userProfile.email },
  user_login_history: { column: 'user_id', value: userProfile.id },
  theme_preferences: { column: 'user_id', value: userProfile.id },
  security_settings: { column: 'user_id', value: userProfile.id },
  password_resets: { column: 'user_id', value: userProfile.id },
  notification_preferences: { column: 'user_id', value: userProfile.id },
  user_profiles: { column: 'email', value: userProfile.email }
});

const SecurityPrivacy = () => {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [settings, setSettings] = useState({
    twoFactorEnabled: false,
    login_notifications: true,
    device_history: true,
    passwordChangeInterval: '90',
    sessionTimeout: '30',
    dataSharing: false,
    activityTracking: true,
    publicProfile: false
  });
  const [lastPasswordChange, setLastPasswordChange] = useState(null);

  // Hesap silme modal state'leri
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [firstNumber, setFirstNumber] = useState(0);
  const [secondNumber, setSecondNumber] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [isDeleteButtonEnabled, setIsDeleteButtonEnabled] = useState(false);

  // Yeni state: Seçilen oturum detaylarını tutar (pop-up için)
  const [selectedLogin, setSelectedLogin] = useState(null);

  // Helper: Tarihi gg/aa/yy formatında döndürür
  const formatDate = (date) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  // Helper: Geçen süreyi hesaplar
  const getTimeDifference = (date) => {
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);

    if (diffSec < 60) return `${diffSec} saniye önce`;
    else if (diffMin < 60) return `${diffMin} dakika önce`;
    else if (diffHour < 24) return `${diffHour} saat önce`;
    else if (diffDay < 30) return `${diffDay} gün önce`;
    else if (diffMonth < 12) return `${diffMonth} ay önce`;
    else return `${diffYear} yıl önce`;
  };

  // Helper: Şifre değiştirme barı genişliğini hesaplar
  const getPasswordBarWidth = () => {
    if (!lastPasswordChange) return '100%';
    const diffMs = new Date() - new Date(lastPasswordChange);
    const diffDays = diffMs / (1000 * 3600 * 24);
    const recommended = parseInt(settings.passwordChangeInterval);
    return Math.max(100 - (diffDays / recommended) * 100, 0) + '%';
  };

  // Helper: Güvenlik skorunu hesaplar (maksimum 100)
  const computeSecurityScore = () => {
    let score = 0;
    if (settings.twoFactorEnabled) score += 30;
    if (settings.login_notifications) score += 20;
    if (settings.device_history) score += 10;
    if (lastPasswordChange) {
      const diffDays = (new Date() - new Date(lastPasswordChange)) / (1000 * 3600 * 24);
      if (diffDays <= parseInt(settings.passwordChangeInterval)) score += 20;
    }
    if (parseInt(settings.sessionTimeout) <= 30) score += 10;
    if (!settings.dataSharing) score += 5;
    if (!settings.activityTracking) score += 5;
    return Math.min(score, 100);
  };

  const securityScore = computeSecurityScore();

  // Kullanıcı verileri ve giriş geçmişini çek
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/signin');
          return;
        }
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        if (profileError) throw profileError;
        setUserProfile(profile);

        const { data: history, error: historyError } = await supabase
          .from('user_login_history')
          .select('*')
          .eq('user_id', session.user.id)
          .order('login_timestamp', { ascending: false })
          .limit(5);
        if (historyError) throw historyError;
        if (history && history.length > 0) {
          // İlk kayıt mevcut oturum olarak alınır
          setCurrentSession(history[0]);
          setLoginHistory(history.slice(1));
        } else {
          setLoginHistory([]);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Kullanıcı bilgileri yüklenirken bir hata oluştu');
      }
    };
    fetchUserData();
  }, [navigate]);

  // Güvenlik ayarlarını çek
  useEffect(() => {
    const fetchSecuritySettings = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/signin');
          return;
        }
        const { data: securityData, error } = await supabase
          .from('security_settings')
          .select('*')
          .eq('user_id', session.user.id)
          .single();
        if (error && error.code !== 'PGRST116') throw error;
        if (securityData) {
          setSettings({
            twoFactorEnabled: securityData.two_factor_enabled,
            login_notifications: securityData.login_notifications,
            device_history: securityData.device_history,
            passwordChangeInterval: securityData.password_change_interval,
            sessionTimeout: securityData.session_timeout,
            dataSharing: securityData.data_sharing,
            activityTracking: securityData.activity_tracking,
            publicProfile: securityData.public_profile
          });
          if (securityData.last_password_change) {
            setLastPasswordChange(securityData.last_password_change);
          } else {
            // Hesabın oluşturulma tarihi kullanılacak
            setLastPasswordChange(session.user.created_at);
          }
        }
      } catch (error) {
        console.error('Error fetching security settings:', error);
        toast.error('Güvenlik ayarları yüklenirken bir hata oluştu');
      }
    };
    fetchSecuritySettings();
  }, [navigate]);

  // Ayarları kaydetme
  const handleSave = async () => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/signin');
        return;
      }
      const { data: existingSettings } = await supabase
        .from('security_settings')
        .select('id')
        .eq('user_id', session.user.id)
        .single();

      const formattedSettings = {
        user_id: session.user.id,
        two_factor_enabled: settings.twoFactorEnabled,
        login_notifications: settings.login_notifications,
        device_history: settings.device_history,
        password_change_interval: settings.passwordChangeInterval,
        session_timeout: settings.sessionTimeout,
        data_sharing: settings.dataSharing,
        activity_tracking: settings.activityTracking,
        public_profile: settings.publicProfile,
        updated_at: new Date().toISOString()
      };

      if (existingSettings) {
        const { error } = await supabase
          .from('security_settings')
          .update(formattedSettings)
          .eq('user_id', session.user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('security_settings')
          .insert([formattedSettings]);
        if (error) throw error;
      }
      toast.success('Güvenlik ayarlarınız başarıyla güncellendi');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Ayarlar kaydedilirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  // Tüm oturumları sonlandırma
  const handleEndAllSessions = async () => {
    try {
      setIsLoading(true);
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ online_status: false })
        .eq('id', userProfile.id);
      
      if (updateError) {
        console.error('Online status güncelleme hatası:', updateError);
        toast.error('Online durum güncellenirken bir hata oluştu');
        return;
      }
  
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;
  
      toast.success('Tüm oturumlar başarıyla sonlandırıldı');
      navigate('/signin');
    } catch (error) {
      console.error('Error ending sessions:', error);
      toast.error('Oturumlar sonlandırılırken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  // Hesap silme modal işlemleri
  const openDeleteModal = () => {
    const num1 = Math.floor(Math.random() * 11) + 10;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setFirstNumber(num1);
    setSecondNumber(num2);
    setUserAnswer('');
    setIsAnswerCorrect(false);
    setCountdown(5);
    setIsDeleteButtonEnabled(false);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    const correctAnswer = firstNumber + secondNumber;
    if (Number(userAnswer) === correctAnswer) {
      setIsAnswerCorrect(true);
    } else {
      setIsAnswerCorrect(false);
      setIsDeleteButtonEnabled(false);
      setCountdown(5);
    }
  }, [userAnswer, firstNumber, secondNumber]);

  useEffect(() => {
    let timer;
    if (isAnswerCorrect && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsDeleteButtonEnabled(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isAnswerCorrect, countdown]);

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setUserAnswer('');
    setIsAnswerCorrect(false);
    setCountdown(5);
    setIsDeleteButtonEnabled(false);
  };

  // Profil fotoğrafı silme
  const deleteProfilePhoto = async (photoPath) => {
    const { error } = await supabase.storage.from('profile_photos').remove([photoPath]);
    if (error) {
      console.error("Profil fotoğrafı silinemedi:", error);
      toast.error("Profil fotoğrafı silinirken bir hata oluştu");
    } else {
      toast.success("Profil fotoğrafı başarıyla silindi");
    }
  };

  // Hesap silme işlemini gerçekleştiren API çağrısı
  const handleDeleteAccount = async () => {
    try {
      setIsLoading(true);
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      if (!session) {
        navigate('/signin');
        return;
      }

      if (userProfile?.profile_photo_url) {
        try {
          const photoUrl = new URL(userProfile.profile_photo_url);
          const segments = photoUrl.pathname.split('/');
          const filePath = segments.slice(6).join('/');
           
          if (filePath) {
            const { error: storageError } = await supabase.storage
              .from('profile-photos')
              .remove([filePath]);

            if (storageError) {
              console.error('Profile photo deletion error:', storageError);
            }
          }
        } catch (photoError) {
          console.error('Error processing profile photo:', photoError);
        }
      }

      const response = await fetch(`${import.meta.env.VITE_LOCALHOST_URL}/api/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: session.user.id,
          email: session.user.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Geçersiz yanıt alındı');
      }

      const result = await response.json();
      localStorage.clear();

      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        console.error('Sign out error:', signOutError);
      }

      toast.success('Hesabınız başarıyla silindi');
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(`Hesap silinirken bir hata oluştu: ${error.message}`);
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Navbar */}
      <div className="sticky top-0 z-10 bg-white/80 border-gray-200 backdrop-blur-xl border-b dark:bg-gray-800/80 dark:border-gray-700 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Güvenlik Ayarları
            </h1>
            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleTheme}
                className={`p-2 rounded-full ${
                  isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } transition-colors`}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } transition-colors`}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Geri Dön</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Üst Başlık */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Güvenlik & Gizlilik
                </h2>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  Hesap güvenliğinizi ve gizlilik tercihlerinizi yönetin.
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSave}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Kaydediliyor...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Değişiklikleri Kaydet</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Güvenlik Durumu */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <Shield className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Güvenlik Durumu
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Hesabınızın güncel güvenlik durumu
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* İki Faktörlü Doğrulama */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">2FA Durumu</span>
                    <span className={`text-sm font-medium ${settings.twoFactorEnabled ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {settings.twoFactorEnabled ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div 
                      className={`h-full ${settings.twoFactorEnabled ? 'bg-green-500' : 'bg-red-500'}`}
                      animate={{ width: settings.twoFactorEnabled ? '100%' : '0%' }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>

                {/* Son Şifre Değişimi */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Son Şifre Değişimi</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {lastPasswordChange 
                        ? `${formatDate(lastPasswordChange)} (${getTimeDifference(lastPasswordChange)})`
                        : 'Bilgi Yok'}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-yellow-500"
                      animate={{ width: getPasswordBarWidth() }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>

                {/* Güvenlik Skoru */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Güvenlik Skoru</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {securityScore}/100
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-blue-500"
                      animate={{ width: `${securityScore}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Son Giriş Aktiviteleri */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Clock className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Son Giriş Aktiviteleri
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Son 5 oturum açma kaydı
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleEndAllSessions}
                  className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                >
                  Tüm Oturumları Sonlandır
                </button>
              </div>
              <div className="p-6">
                {currentSession && (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                          <CheckCircle2 className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            Mevcut Oturum
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {currentSession.location || 'Bilinmeyen Konum'} - {new Date(currentSession.login_timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {currentSession.device_info?.device_name || 'Bilinmeyen Cihaz'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {currentSession.ip_address}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loginHistory.map((login, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedLogin(login)}
                      className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${
                            login.success 
                              ? 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                              : 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                          }`}>
                            {login.success ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {login.location || 'Bilinmeyen Konum'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(login.login_timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {login.device_info?.device_name || 'Bilinmeyen Cihaz'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {login.ip_address}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Güvenlik Ayarları */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <Lock className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Güvenlik Ayarları
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Hesap güvenlik tercihlerinizi özelleştirin
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* İki Faktörlü Doğrulama */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Key className="w-5 h-5 text-gray-400" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        İki Faktörlü Doğrulama (2FA)
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Hesabınıza ekstra güvenlik katmanı ekleyin
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, twoFactorEnabled: !prev.twoFactorEnabled }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                      settings.twoFactorEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <motion.span
                      initial={false}
                      animate={{ translateX: settings.twoFactorEnabled ? '20px' : '2px' }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      className="inline-block h-5 w-5 transform rounded-full bg-white"
                    />
                  </button>
                </div>

                {/* Giriş Bildirimleri */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Smartphone className="w-5 h-5 text-gray-400" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        Giriş Bildirimleri
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Yeni bir cihazdan giriş yapıldığında bildirim alın
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, login_notifications: !prev.login_notifications }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                      settings.login_notifications ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <motion.span
                      initial={false}
                      animate={{ translateX: settings.login_notifications ? '20px' : '2px' }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      className="inline-block h-5 w-5 transform rounded-full bg-white"
                    />
                  </button>
                </div>

                {/* Şifre Değişim Aralığı */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Şifre Değişim Aralığı
                  </label>
                  <select
                    value={settings.passwordChangeInterval}
                    onChange={(e) => setSettings(prev => ({ ...prev, passwordChangeInterval: e.target.value }))}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="30">30 gün</option>
                    <option value="60">60 gün</option>
                    <option value="90">90 gün</option>
                    <option value="180">180 gün</option>
                  </select>
                </div>

                {/* Oturum Zaman Aşımı */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Oturum Zaman Aşımı
                  </label>
                  <select
                    value={settings.sessionTimeout}
                    onChange={(e) => setSettings(prev => ({ ...prev, sessionTimeout: e.target.value }))}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="15">15 dakika</option>
                    <option value="30">30 dakika</option>
                    <option value="60">1 saat</option>
                    <option value="120">2 saat</option>
                  </select>
                </div>
              </div>
            </motion.div>

            {/* Gizlilik Ayarları */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <Eye className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Gizlilik Ayarları
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Veri paylaşımı ve gizlilik tercihlerinizi yönetin
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {/* Veri Paylaşımı */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Globe className="w-5 h-5 text-gray-400" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        Veri Paylaşımı
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Anonim kullanım verilerini paylaşarak uygulamayı geliştirmemize yardımcı olun
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, dataSharing: !prev.dataSharing }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                      settings.dataSharing ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <motion.span
                      initial={false}
                      animate={{ translateX: settings.dataSharing ? '20px' : '2px' }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      className="inline-block h-5 w-5 transform rounded-full bg-white"
                    />
                  </button>
                </div>

                {/* Aktivite İzleme */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <UserX className="w-5 h-5 text-gray-400" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        Aktivite İzleme
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Uygulama içi aktivitelerinizin izlenmesine izin verin
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, activityTracking: !prev.activityTracking }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                      settings.activityTracking ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <motion.span
                      initial={false}
                      animate={{ translateX: settings.activityTracking ? '20px' : '2px' }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      className="inline-block h-5 w-5 transform rounded-full bg-white"
                    />
                  </button>
                </div>

                {/* Profil Görünürlüğü */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Eye className="w-5 h-5 text-gray-400" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        Profil Görünürlüğü
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Profilinizin diğer kullanıcılar tarafından görüntülenmesine izin verin
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, publicProfile: !prev.publicProfile }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                      settings.publicProfile ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <motion.span
                      initial={false}
                      animate={{ translateX: settings.publicProfile ? '20px' : '2px' }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      className="inline-block h-5 w-5 transform rounded-full bg-white"
                    />
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Hesap Silme Bölümü */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/40">
                    <UserX className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-red-900 dark:text-red-300">
                      Hesabı Sil
                    </h3>
                    <p className="text-sm text-red-700 dark:text-red-400">
                      Bu işlem geri alınamaz. Tüm verileriniz kalıcı olarak silinecektir.
                    </p>
                  </div>
                </div>
                <button
                  onClick={openDeleteModal}
                  className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                >
                  Hesabı Sil
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Hesap Silme Modal (Glassmorphism Efektli) */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            {/* Arka plan bulanıklaştırma */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={handleCancelDelete}
            />
            {/* Modal İçeriği */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="relative z-50 bg-white dark:bg-gray-800 bg-opacity-60 dark:bg-opacity-60 backdrop-blur-md border border-white/30 dark:border-gray-700/50 rounded-2xl shadow-xl p-6 max-w-md mx-4"
            >
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Hesabınızı Silmek İstediğinize Emin misiniz?
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Bu işlem geri alınamaz! Hesabınız silindiğinde; doğrulama kodları, giriş geçmişi, tema ve güvenlik ayarları, şifre sıfırlama istekleri ve bildirim tercihleri dahil tüm verileriniz kalıcı olarak silinecektir.
              </p>
              <div className="mb-4">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  Lütfen aşağıdaki doğrulama sorusunu yanıtlayın:
                </p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {firstNumber} + {secondNumber} = ?
                </p>
                <input
                  type="number"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  className="mt-2 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Cevabınızı yazın"
                />
                {userAnswer !== '' && (
                  isAnswerCorrect ? (
                    <p className="text-green-600 mt-1 text-sm">
                      Doğru Bildiniz! {countdown > 0 ? `Buton ${countdown} saniye sonra aktif olacak.` : 'Buton aktif!'}
                    </p>
                  ) : (
                    <p className="text-red-600 mt-1 text-sm">Yanlış cevap, lütfen tekrar deneyin.</p>
                  )
                )}
              </div>
              <div className="flex justify-end gap-4">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Vazgeç
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={!isDeleteButtonEnabled}
                  className={`px-4 py-2 rounded-lg ${
                    isDeleteButtonEnabled
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-red-300 text-white cursor-not-allowed'
                  } transition-colors`}
                >
                  Evet, sil
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cihaz Detayları Modal */}
      <AnimatePresence>
        {selectedLogin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            {/* Arka Plan */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setSelectedLogin(null)}
            />
            {/* Modal İçeriği */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="relative z-50 bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl mx-4"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Smartphone className="w-5 h-5" />
                    Cihaz Detayları
                  </h2>
                  <button
                    onClick={() => setSelectedLogin(null)}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Konum ve Zaman Bilgileri */}
                  <div className="space-y-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-primary-500" />
                        Konum ve Zaman
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Lokasyon</span>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedLogin.location || 'Bilinmeyen Konum'}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Zaman Dilimi</span>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedLogin.device_info?.time_zone || 'Bilinmiyor'}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Giriş Zamanı</span>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {new Date(selectedLogin.login_timestamp).toLocaleString('tr-TR', {
                              dateStyle: 'medium',
                              timeStyle: 'medium'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <Globe className="w-4 h-4 text-primary-500" />
                        Ağ Bilgileri
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">IP Adresi</span>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedLogin.ip_address}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cihaz ve Tarayıcı Bilgileri */}
                  <div className="space-y-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <Monitor className="w-4 h-4 text-primary-500" />
                        Cihaz Bilgileri
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Cihaz Tipi</span>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedLogin.device_info?.deviceType || 'Bilinmiyor'}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Tarayıcı</span>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedLogin.device_info?.browser || 'Bilinmiyor'}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">İşletim Sistemi</span>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedLogin.device_info?.os || 'Bilinmiyor'}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Ekran Çözünürlüğü</span>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedLogin.device_info?.screenResolution || 'Bilinmiyor'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <Globe className="w-4 h-4 text-primary-500" />
                        Tarayıcı Bilgileri
                      </h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Tarayıcı</span>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedLogin.device_info?.web_browser_name || 'Bilinmiyor'}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Tarayıcı Versiyonu</span>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedLogin.device_info?.browser_version || 'Bilinmiyor'}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Tarayıcı Dili</span>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedLogin.device_info?.browser_language || 'Bilinmiyor'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* User Agent Bilgisi */}
                <div className="mt-16">
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Code className="w-4 h-4 text-primary-500" />
                      Kullanıcı Tarayıcı Bilgisi
                    </h3>
                    <p className="text-xs font-mono text-gray-600 dark:text-gray-400 break-all">
                      {selectedLogin.user_agent || 'Bilinmiyor'}
                    </p>
                  </div>
                </div>

                {/* Kapat Butonu */}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setSelectedLogin(null)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SecurityPrivacy;
